// Analysis results, analysis job status API calls.

import { apiFetch } from "./apiClient";

import type {
    AnalysisJob,
    AnalysisResults,
    AnalysisResultsResponse,
    PropertyClassification,
} from "../types/analysis";

export type { AnalysisJob, AnalysisResults, AnalysisResultsResponse, PropertyClassification };

export async function startPropertyAnalysis(): Promise<{ id: number; status: string }> {
    const res = await apiFetch("/api/property-analysis/start", {
        method: "POST",
    });
    if (!res.ok) throw new Error(`startPropertyAnalysis failed: ${res.status}`);
    return res.json();
}

export async function fetchAnalysisStatus(): Promise<{ success: boolean; job: AnalysisJob | null }> {
    const res = await apiFetch("/api/property-analysis/status");
    if (!res.ok) throw new Error(`fetchAnalysisStatus failed: ${res.status}`);
    return res.json();
}

export async function fetchAnalysisResults(): Promise<AnalysisResultsResponse> {
    const res = await apiFetch("/api/property-analysis/results");
    if (!res.ok) throw new Error(`fetchAnalysisResults failed: ${res.status}`);
    return res.json();
}

export async function clearAnalysisJob(): Promise<void> {
    const res = await apiFetch("/api/property-analysis/clear", { method: "POST" });
    if (!res.ok) throw new Error(`clearAnalysisJob failed: ${res.status}`);
}

export async function fetchAnalysisHasCompleted(): Promise<boolean> {
    const res = await apiFetch("/api/property-analysis/has-completed");
    if (!res.ok) throw new Error(`fetchAnalysisHasCompleted failed: ${res.status}`);
    const data = await res.json();
    return data.hasCompleted === true;
}

export interface StrategyRecommendation {
    strategyName: string;
    description: string;
    params: Record<string, unknown> & {
        fridayAdjustmentPct?: number;
        saturdayAdjustmentPct?: number;
        sundayAdjustmentPct?: number;
    };
    kpiBaselines: {
        occupancyRate: number;
        basePrice: number;
        minimalAcceptablePrice: number;
        totalRevenue: number;
    };
    roomKindSummaries: Array<{
        roomKindId: string;
        occupancyRate: number;
        basePrice: number;
        minimalAcceptablePrice: number;
        maxPrice: number;
        tier: string;
    }>;
    monthlyBaseline: Array<{
        month: number;
        monthName: string;
        occupancyRate: number;
        basePrice: number;
        minimalAcceptablePrice: number;
    }>;
    bookingWindow: {
        avgLeadTimeDays: number;
        medianLeadTimeDays: number;
    };
}

export async function fetchStrategyRecommendation(): Promise<StrategyRecommendation> {
    const res = await apiFetch("/api/property-analysis/strategy-recommendation");
    if (!res.ok) throw new Error(`fetchStrategyRecommendation failed: ${res.status}`);
    const data = await res.json();
    return data.recommendation;
}

export interface PollStatus {
    isPolling: boolean;
    lastPollAt: string | null;
    nextPollAt: string | null;
    lastChangedDates: string[];
}

export async function fetchPollStatus(): Promise<PollStatus> {
    const res = await apiFetch("/api/property-analysis/poll-status");
    if (!res.ok) throw new Error(`fetchPollStatus failed: ${res.status}`);
    return res.json();
}

export async function classifyProperty(
    signal?: AbortSignal,
    locationAddress?: string,
): Promise<PropertyClassification | null> {
    const res = await apiFetch("/api/property-analysis/classify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locationAddress: locationAddress || undefined }),
        signal,
    });
    if (!res.ok) throw new Error(`classifyProperty failed: ${res.status}`);
    const data = await res.json();
    return data.classification ?? null;
}

